<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import AgentsAPI from 'dashboard/api/agents';
import TalkviaProposalsAPI from 'dashboard/api/talkviaProposals';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();

const proposal = ref(null);
const agents = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const editStatus = ref('');
const editSellerId = ref('');
const bankingOpen = ref(true);
const creditOpen = ref(false);

const copy = {
  back: 'Esteira de propostas',
  loading: 'Carregando proposta...',
  notFound: 'Proposta não encontrada.',
  sections: {
    client: 'Cliente',
    proposal: 'Dados da proposta',
    rejection: 'Motivo da recusa',
    banking: 'Dados bancários',
    credit: 'Crédito / simulação',
    activity: 'Atividade',
    quickEdit: 'Atualizar proposta',
  },
  fields: {
    name: 'Nome',
    cpf: 'CPF',
    phone: 'Telefone',
    seller: 'Vendedor',
    conversation: 'Conversa no CRM',
    amount: 'Valor liberado',
    product: 'Produto',
    bank: 'Banco',
    stage: 'Fase',
    status: 'Status',
    term: 'Prazo',
    paymentDate: 'Pgto ao cliente',
    closeDate: 'Previsão fechamento',
    followUp: 'Próx. follow-up',
    createdAt: 'Criada em',
    updatedAt: 'Atualizada em',
    inbox: 'Caixa de entrada',
  },
  actions: {
    save: 'Salvar alterações',
    pdf: 'PDF',
    edit: 'Editar',
    signature: 'Assinatura',
    saving: 'Salvando...',
  },
  statuses: {
    em_andamento: 'Em andamento',
    aguardando_cliente: 'Aguardando cliente',
    aguardando_banco: 'Aguardando banco',
    aguardando_assinatura: 'Aguardando assinatura',
    aprovado: 'Aprovado',
    pago: 'Pago',
    encerrado: 'Encerrado',
    recusado: 'Recusado',
    cancelado: 'Cancelado',
  },
  phases: {
    novo: 'Nova',
    simulacao: 'Simulação',
    documentacao: 'Documentação',
    assinatura: 'Assinatura',
    pagamento: 'Pagamento',
    finalizada: 'Finalizada',
  },
  messages: {
    loadFailed: 'Não foi possível carregar a proposta.',
    saveFailed: 'Não foi possível salvar.',
    saved: 'Proposta atualizada.',
    pdfFailed: 'Não foi possível gerar o PDF.',
    noActivity: 'Sem atividade registrada.',
  },
};

const statusOptions = Object.entries(copy.statuses).map(([value, label]) => ({
  value,
  label,
}));

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});
const formatMoney = v => moneyFormatter.format(Number(v || 0));
const formatDate = v => (v ? new Date(v).toLocaleDateString('pt-BR') : '-');
const formatDateTime = v => (v ? new Date(v).toLocaleString('pt-BR') : '-');
const displayName = u => u?.name || u?.available_name || u?.email || '-';
const agentOptions = computed(() => [
  { value: '', label: 'Sem vendedor' },
  ...agents.value.map(agent => ({
    value: agent.id,
    label: displayName(agent),
  })),
]);

const statusLabel = v => copy.statuses[v] || v || '-';
const phaseLabel = v => copy.phases[v] || v || '-';

const statusBadgeClass = value =>
  ({
    em_andamento: 'bg-n-blue-3 text-n-blue-11 border-n-blue-6',
    aguardando_cliente: 'bg-n-amber-3 text-n-amber-11 border-n-amber-6',
    aguardando_banco: 'bg-n-purple-3 text-n-purple-11 border-n-purple-6',
    aguardando_assinatura: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6',
    aprovado: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6',
    pago: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6',
    encerrado: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6',
    recusado: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6',
    cancelado: 'bg-n-slate-3 text-n-slate-11 border-n-weak',
  })[value] || 'bg-n-slate-3 text-n-slate-11 border-n-weak';

const backUrl = computed(() => `/app/accounts/${accountId.value}/propostas`);

const conversationUrl = computed(() => {
  if (!proposal.value?.conversation_display_id) return null;
  return `/app/accounts/${accountId.value}/conversations/${proposal.value.conversation_display_id}`;
});

const timeline = computed(() => {
  const t = proposal.value?.metadata?.timeline || [];
  return [...t].reverse();
});

const bankingPayload = computed(() => {
  const p = proposal.value?.banking_payload;
  if (!p || typeof p !== 'object' || !Object.keys(p).length) return null;
  return p;
});

const selectedSimulation = computed(
  () => bankingPayload.value?.selected_simulation?.data || null
);

const creditPayload = computed(() => {
  const p = proposal.value?.credit_payload;
  if (!p || typeof p !== 'object' || !Object.keys(p).length) return null;
  return p;
});

const keyValuePairs = obj => {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .filter(
      ([, v]) =>
        v !== null && v !== undefined && v !== '' && typeof v !== 'object'
    )
    .map(([k, v]) => ({ key: k, value: String(v) }));
};

const flatBankingRows = computed(() => keyValuePairs(selectedSimulation.value));

const lastBankingEvent = computed(() => {
  const events = bankingPayload.value?.status_events;
  if (!Array.isArray(events) || !events.length) return null;
  return events[events.length - 1];
});

const rejectionReason = computed(() => {
  if (!['recusado', 'cancelado'].includes(proposal.value?.status)) return null;
  return (
    bankingPayload.value?.rejection_reason_label ||
    bankingPayload.value?.rejection_reason ||
    lastBankingEvent.value?.reason ||
    lastBankingEvent.value?.descricao ||
    null
  );
});

const loadProposal = async () => {
  isLoading.value = true;
  try {
    const [proposalRes, agentsRes] = await Promise.allSettled([
      TalkviaProposalsAPI.getById(route.params.id),
      AgentsAPI.get(),
    ]);
    if (proposalRes.status === 'fulfilled') {
      proposal.value = proposalRes.value.data;
      editStatus.value = proposal.value.status;
      editSellerId.value = proposal.value.seller_id || '';
    } else {
      useAlert(copy.messages.loadFailed);
    }
    if (agentsRes.status === 'fulfilled') {
      agents.value = agentsRes.value.data || [];
    }
  } catch {
    useAlert(copy.messages.loadFailed);
  } finally {
    isLoading.value = false;
  }
};

const saveChanges = async () => {
  if (!proposal.value) return;
  isSaving.value = true;
  try {
    const res = await TalkviaProposalsAPI.update(proposal.value.id, {
      status: editStatus.value,
      seller_id: editSellerId.value || null,
    });
    proposal.value = res.data;
    useAlert(copy.messages.saved);
  } catch {
    useAlert(copy.messages.saveFailed);
  } finally {
    isSaving.value = false;
  }
};

const escapeHtml = v =>
  String(v || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const printProposal = () => {
  if (!proposal.value) return;
  const p = proposal.value;
  const timelineHtml = timeline.value
    .map(
      item =>
        `<li><strong>${escapeHtml(formatDateTime(item.created_at))}</strong> — ${escapeHtml(item.message)}</li>`
    )
    .join('');
  const content = `<html><head><title>${escapeHtml(p.title)}</title>
    <style>body{font-family:Arial,sans-serif;color:#0f172a;margin:32px}h2{margin:20px 0 8px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:16px}.box{border:1px solid #e2e8f0;border-radius:8px;padding:12px}.label{color:#64748b;font-size:12px;text-transform:uppercase}.value{margin-top:4px;font-size:15px;font-weight:600}ul{line-height:1.8}</style>
    </head><body>
    <h1>${escapeHtml(p.title)}</h1>
    <p style="color:#64748b">Espelho gerado pela Talkvia · ${formatDateTime(new Date().toISOString())}</p>
    <div class="grid">
      <div class="box"><div class="label">Cliente</div><div class="value">${escapeHtml(p.contact?.name || '-')}</div></div>
      <div class="box"><div class="label">CPF</div><div class="value">${escapeHtml(p.client_cpf || '-')}</div></div>
      <div class="box"><div class="label">Produto</div><div class="value">${escapeHtml(p.product?.name || '-')}</div></div>
      <div class="box"><div class="label">Valor</div><div class="value">${escapeHtml(formatMoney(p.amount))}</div></div>
      <div class="box"><div class="label">Status</div><div class="value">${escapeHtml(statusLabel(p.status))}</div></div>
      <div class="box"><div class="label">Vendedor</div><div class="value">${escapeHtml(displayName(p.seller))}</div></div>
      ${rejectionReason.value ? `<div class="box"><div class="label">${escapeHtml(copy.sections.rejection)}</div><div class="value">${escapeHtml(rejectionReason.value)}</div></div>` : ''}
    </div>
    <h2>Linha do tempo</h2><ul>${timelineHtml || '<li>Sem eventos.</li>'}</ul>
    </body></html>`;
  const w = window.open('', '_blank');
  if (!w) {
    useAlert(copy.messages.pdfFailed);
    return;
  }
  w.document.open();
  w.document.write(content);
  w.document.close();
  w.focus();
  window.setTimeout(() => w.print(), 250);
};

onMounted(loadProposal);
</script>

<template>
  <section class="h-full w-full overflow-y-auto bg-n-surface-1">
    <!-- Breadcrumb / header bar -->
    <div
      class="sticky top-0 z-10 border-b border-n-weak bg-n-surface-1/95 backdrop-blur-sm px-6 py-3"
    >
      <div class="mx-auto flex w-full max-w-6xl items-center gap-3">
        <a
          :href="backUrl"
          class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click.prevent="router.push(backUrl)"
        >
          <span class="i-lucide-arrow-left size-4" />
          {{ copy.back }}
        </a>
        <span class="text-n-slate-8">/</span>
        <span class="text-sm font-medium text-n-slate-12 truncate max-w-xs">
          {{ proposal?.title || '...' }}
        </span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex h-64 items-center justify-center">
      <span class="i-lucide-loader-2 size-6 animate-spin text-n-slate-10" />
    </div>

    <!-- Not found -->
    <div v-else-if="!proposal" class="flex h-64 items-center justify-center">
      <p class="text-sm text-n-slate-11">{{ copy.notFound }}</p>
    </div>

    <!-- Content -->
    <div v-else class="mx-auto w-full max-w-6xl px-6 py-6 flex flex-col gap-6">
      <!-- Page title + badges + actions -->
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="min-w-0">
          <h1 class="text-xl font-semibold text-n-slate-12 mb-2">
            {{ proposal.title }}
          </h1>
          <div class="flex flex-wrap items-center gap-2">
            <span
              class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold"
              :class="statusBadgeClass(proposal.status)"
            >
              <span class="h-1.5 w-1.5 rounded-full bg-current" />
              {{ statusLabel(proposal.status) }}
            </span>
            <span
              v-if="proposal.product?.name"
              class="rounded-full border border-n-weak bg-n-alpha-1 px-3 py-1 text-xs font-medium text-n-slate-11"
            >
              {{ proposal.product.name }}
            </span>
            <span
              v-if="proposal.product?.provider"
              class="rounded-full border border-n-weak bg-n-alpha-1 px-3 py-1 text-xs font-medium text-n-slate-11"
            >
              {{ proposal.product.provider }}
            </span>
            <span
              class="rounded-full border border-n-weak bg-n-alpha-1 px-3 py-1 text-xs text-n-slate-10"
            >
              {{ phaseLabel(proposal.stage) }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            class="inline-flex h-9 items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click="printProposal"
          >
            <span class="i-lucide-printer size-4" />
            {{ copy.actions.pdf }}
          </button>
          <a
            v-if="proposal.signature_url"
            :href="proposal.signature_url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex h-9 items-center gap-1.5 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95 transition-all"
          >
            <span class="i-lucide-pen-line size-4" />
            {{ copy.actions.signature }}
          </a>
        </div>
      </div>

      <!-- Main 2-column layout -->
      <div class="grid gap-6 lg:grid-cols-[1fr_320px]">
        <!-- LEFT: data cards -->
        <div class="flex flex-col gap-5 min-w-0">
          <!-- Client card -->
          <div class="rounded-xl border border-n-weak bg-n-solid-2">
            <div
              class="flex items-center gap-2 border-b border-n-weak px-5 py-3"
            >
              <span class="i-lucide-user size-4 text-n-slate-10" />
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ copy.sections.client }}
              </h2>
            </div>
            <div class="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.name }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ proposal.contact?.name || '-' }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.cpf }}
                </p>
                <p class="text-sm font-medium text-n-slate-12 font-mono">
                  {{ proposal.client_cpf || '-' }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.phone }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ proposal.contact?.phone_number || '-' }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.seller }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ displayName(proposal.seller) }}
                </p>
              </div>
              <div v-if="proposal.inbox?.name">
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.inbox }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ proposal.inbox.name }}
                </p>
              </div>
              <div v-if="conversationUrl">
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.conversation }}
                </p>
                <a
                  :href="conversationUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 text-sm font-medium text-n-blue-10 hover:underline"
                >
                  <span class="i-lucide-external-link size-3.5" />
                  #{{ proposal.conversation_display_id }}
                </a>
              </div>
            </div>
          </div>

          <!-- Proposal card -->
          <div class="rounded-xl border border-n-weak bg-n-solid-2">
            <div
              class="flex items-center gap-2 border-b border-n-weak px-5 py-3"
            >
              <span class="i-lucide-file-text size-4 text-n-slate-10" />
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ copy.sections.proposal }}
              </h2>
            </div>
            <div class="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.amount }}
                </p>
                <p class="text-base font-semibold text-n-teal-10">
                  {{ formatMoney(proposal.amount) }}
                </p>
              </div>
              <div v-if="proposal.product?.name">
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.product }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ proposal.product.name }}
                </p>
              </div>
              <div v-if="proposal.term">
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.term }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ proposal.term }}
                </p>
              </div>
              <div v-if="proposal.client_payment_date">
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.paymentDate }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ formatDate(proposal.client_payment_date) }}
                </p>
              </div>
              <div v-if="proposal.expected_close_date">
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.closeDate }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ formatDate(proposal.expected_close_date) }}
                </p>
              </div>
              <div v-if="proposal.next_follow_up_at">
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.followUp }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ formatDateTime(proposal.next_follow_up_at) }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.createdAt }}
                </p>
                <p class="text-sm text-n-slate-11">
                  {{ formatDateTime(proposal.created_at) }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ copy.fields.updatedAt }}
                </p>
                <p class="text-sm text-n-slate-11">
                  {{ formatDateTime(proposal.updated_at) }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-if="rejectionReason"
            class="rounded-xl border border-n-ruby-6 bg-n-ruby-2/30 px-5 py-4"
          >
            <div class="flex items-start gap-3">
              <span class="i-lucide-circle-x size-5 shrink-0 text-n-ruby-10" />
              <div>
                <p class="text-xs font-medium text-n-ruby-10 mb-1">
                  {{ copy.sections.rejection }}
                </p>
                <p class="text-sm text-n-slate-12">{{ rejectionReason }}</p>
              </div>
            </div>
          </div>

          <!-- Banking data collapsible -->
          <div
            v-if="bankingPayload"
            class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden"
          >
            <button
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b px-5 py-3 text-left transition-colors hover:bg-n-alpha-1"
              :class="bankingOpen ? 'border-n-weak' : 'border-transparent'"
              @click="bankingOpen = !bankingOpen"
            >
              <div class="flex items-center gap-2">
                <span class="i-lucide-landmark size-4 text-n-slate-10" />
                <h2 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.sections.banking }}
                </h2>
                <span
                  v-if="lastBankingEvent"
                  class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
                >
                  {{
                    lastBankingEvent.tipo || lastBankingEvent.descricao || ''
                  }}
                </span>
              </div>
              <span
                class="i-lucide-chevron-down size-4 text-n-slate-9 transition-transform"
                :class="bankingOpen ? 'rotate-180' : ''"
              />
            </button>
            <div v-if="bankingOpen" class="p-5">
              <!-- Last banking event -->
              <div
                v-if="lastBankingEvent"
                class="mb-4 rounded-lg border border-n-amber-6 bg-n-amber-2/30 px-4 py-3 text-sm"
              >
                <p class="font-semibold text-n-amber-11 mb-1">
                  {{ lastBankingEvent.tipo || 'Evento bancário' }}
                </p>
                <p
                  v-if="lastBankingEvent.descricao"
                  class="text-xs text-n-slate-11"
                >
                  {{
                    lastBankingEvent.codigo_status != null
                      ? `${lastBankingEvent.codigo_status} – `
                      : ''
                  }}{{ lastBankingEvent.descricao }}
                </p>
                <p
                  v-if="lastBankingEvent.data"
                  class="mt-1 text-xs text-n-slate-10"
                >
                  {{ formatDateTime(lastBankingEvent.data) }}
                </p>
              </div>

              <!-- Key-value pairs from selected simulation data -->
              <div
                v-if="flatBankingRows.length"
                class="rounded-lg border border-n-weak overflow-hidden"
              >
                <div
                  v-for="(row, i) in flatBankingRows"
                  :key="row.key"
                  class="grid grid-cols-[minmax(11rem,1fr)_minmax(0,1.5fr)] gap-3 px-4 py-2.5 text-sm"
                  :class="i % 2 === 0 ? 'bg-n-alpha-1' : ''"
                >
                  <span
                    class="text-xs text-n-slate-10 uppercase tracking-wide self-center"
                    >{{ row.key }}</span
                  >
                  <span class="font-medium text-n-slate-12 break-all">{{
                    row.value
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Credit payload collapsible -->
          <div
            v-if="creditPayload"
            class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden"
          >
            <button
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b px-5 py-3 text-left transition-colors hover:bg-n-alpha-1"
              :class="creditOpen ? 'border-n-weak' : 'border-transparent'"
              @click="creditOpen = !creditOpen"
            >
              <div class="flex items-center gap-2">
                <span class="i-lucide-calculator size-4 text-n-slate-10" />
                <h2 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.sections.credit }}
                </h2>
              </div>
              <span
                class="i-lucide-chevron-down size-4 text-n-slate-9 transition-transform"
                :class="creditOpen ? 'rotate-180' : ''"
              />
            </button>
            <div v-if="creditOpen" class="p-5">
              <pre
                class="text-xs text-n-slate-11 overflow-auto rounded-lg bg-n-alpha-1 p-4 max-h-72"
                >{{ JSON.stringify(creditPayload, null, 2) }}</pre
              >
            </div>
          </div>
        </div>

        <!-- RIGHT: sidebar -->
        <div class="flex flex-col gap-5">
          <!-- Quick update -->
          <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
            <div class="flex items-center gap-2 mb-4">
              <span class="i-lucide-pencil size-4 text-n-slate-10" />
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ copy.sections.quickEdit }}
              </h2>
            </div>
            <div class="flex flex-col gap-3">
              <label class="flex flex-col gap-1 text-xs text-n-slate-11">
                {{ copy.fields.status }}
                <ComboBox
                  v-model="editStatus"
                  :options="statusOptions"
                  teleport-to-body
                />
              </label>
              <label class="flex flex-col gap-1 text-xs text-n-slate-11">
                {{ copy.fields.seller }}
                <ComboBox
                  v-model="editSellerId"
                  :options="agentOptions"
                  teleport-to-body
                />
              </label>
              <Button
                :label="copy.actions.save"
                :is-loading="isSaving"
                :disabled="isSaving || !editStatus"
                @click="saveChanges"
              />
            </div>
          </div>

          <!-- Activity timeline -->
          <div
            class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden"
          >
            <div
              class="flex items-center gap-2 border-b border-n-weak px-5 py-3"
            >
              <span class="i-lucide-activity size-4 text-n-slate-10" />
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ copy.sections.activity }}
              </h2>
              <span
                class="ml-auto rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10"
              >
                {{ timeline.length }}
              </span>
            </div>
            <div class="p-4">
              <div
                v-if="!timeline.length"
                class="py-4 text-center text-xs text-n-slate-10"
              >
                {{ copy.messages.noActivity }}
              </div>
              <ol v-else class="relative space-y-0">
                <li
                  v-for="(item, i) in timeline"
                  :key="`${item.created_at}-${i}`"
                  class="flex gap-3 pb-4 last:pb-0"
                >
                  <!-- Timeline line + dot -->
                  <div class="flex flex-col items-center">
                    <span
                      class="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full border-2"
                      :class="
                        i === 0
                          ? 'border-n-teal-8 bg-n-teal-5'
                          : 'border-n-slate-6 bg-n-surface-1'
                      "
                    />
                    <div
                      v-if="i < timeline.length - 1"
                      class="mt-1 w-px flex-1 bg-n-weak"
                    />
                  </div>
                  <!-- Content -->
                  <div class="min-w-0 flex-1 pb-1">
                    <p
                      class="text-xs font-medium text-n-slate-12 leading-relaxed"
                    >
                      {{ item.message }}
                    </p>
                    <p class="mt-0.5 text-xs text-n-slate-10">
                      {{ formatDateTime(item.created_at) }}
                    </p>
                    <span
                      v-if="item.status"
                      class="mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium"
                      :class="statusBadgeClass(item.status)"
                    >
                      {{ statusLabel(item.status) }}
                    </span>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
